import React, { useEffect, useState, useMemo } from 'react';
import { useLazyQuery } from '@apollo/client';
import { Helmet } from 'react-helmet';
import { useTable, useSortBy, useFilters } from 'react-table';
import PropTypes from 'prop-types';
import { LIST_PENDANTS } from '../graphql/queries/listPendants';
import '../styles/deviceListPage.css';

const PENDANTS_PER_PAGE = 500;

const FilterInput = ({ column: { filterValue, setFilter } }) => (
  <input
    value={filterValue || ''}
    onChange={e => setFilter(e.target.value || undefined)}
    placeholder="Filter..."
    onClick={e => e.stopPropagation()}
  />
);
FilterInput.propTypes = {
  column: PropTypes.shape({
    filterValue: PropTypes.string,
    setFilter: PropTypes.func.isRequired,
  }).isRequired,
};

const PendantListPage = () => {
  const [pendants, setPendants] = useState([]);
  const [loadingMessage, setLoadingMessage] = useState('Loading...');
  const [fetchSystems] = useLazyQuery(LIST_PENDANTS, {
    fetchPolicy: 'network-only',
  });

  const columns = useMemo(() => [
    { Header: 'Pendant ID', accessor: 'sensor_id', Filter: FilterInput },
    {
      Header: 'Label',
      accessor: row => {
        const id = row.sensor_id || '';
        if (id.length >= 24) {
          return id[0] + id[1] + id[4] + id[5] + id[22] + id[23];
        }
        return '';
      },
      id: 'label',
      Filter: FilterInput,
      Cell: ({ value }) => (
        <span>
          {value}
          {value && (
            <span
              title="If the pendant was supplied after Sep 2025, this code will appear on its label"
              style={{ marginLeft: '4px', cursor: 'help', opacity: 0.6 }}
            >
              ⓘ
            </span>
          )}
        </span>
      ),
    },
    { Header: 'Pendant Zone', accessor: 'zone', Filter: FilterInput },
    { Header: 'System ID', accessor: 'system_id', Filter: FilterInput },
    { Header: 'PERS ID', accessor: 'pers_id', Filter: FilterInput },
    { Header: 'System Status', accessor: 'system_state', Filter: FilterInput },
    { Header: 'Region', accessor: 'pers_region', Filter: FilterInput },
    { Header: 'Last Heartbeat', accessor: 'last_heartbeat_utc', Filter: FilterInput },
    { Header: 'Pairing Date', accessor: 'pairing_date', Filter: FilterInput },
  ], []);

  useEffect(() => {
    const fetchAllPendants = async () => {
      let allPendants = [];
      let nextToken = null;
      let allSystems = [];
      try {
        do {
          const result = await fetchSystems({
            variables: {
              limit: PENDANTS_PER_PAGE,
              nextToken
            }
          });
          if (result.errors) {
            console.error('GraphQL Errors:', result.errors);
          }
          if (result.data?.listSystems) {
            allSystems = [...allSystems, ...result.data.listSystems.items];
            nextToken = result.data.listSystems.nextToken;
            setLoadingMessage(`${allSystems.length} systems loaded...`);
          } else {
            nextToken = null;
          }
        } while (nextToken);

        // Flatten pendant data
        allSystems.forEach(system => {
          const systemId = system.system_id;
          const persId = system.configuration?.pers_id || '';
          const systemState = system.basic_info?.system_status?.system_state || '';
          const region = system.advanced_info?.organisation_setting?.pers_region || '';
          const pendantStatuses = Array.isArray(system.basic_info?.pendant_status)
            ? system.basic_info.pendant_status
            : system.basic_info?.pendant_status ? [system.basic_info.pendant_status] : [];
          const pairingEvents = system.advanced_info?.sensor_pairing_events?.items || [];

          pendantStatuses.forEach(pendant => {
            if (!pendant?.sensor_id) return;
            // Find matching pairing event
            const pairing = pairingEvents.find(ev => ev.sensor_id === pendant.sensor_id);
            allPendants.push({
              sensor_id: pendant.sensor_id,
              zone: pendant.zone || '',
              system_id: systemId,
              pers_id: persId,
              system_state: systemState,
              pers_region: region,
              last_heartbeat_utc: pendant.last_heartbeat_utc || '',
              pairing_date: pairing?.timestamp_utc || '',
            });
          });
        });
        setPendants(allPendants);
        setLoadingMessage(`${allPendants.length} pendants loaded.`);
      } catch (e) {
        console.error('Fetching pendants failed:', e);
        setLoadingMessage('Failed to load pendants.');
      }
    };
    fetchAllPendants();
    // eslint-disable-next-line
  }, []);

  const tableInstance = useTable(
    { columns, data: pendants },
    useFilters,
    useSortBy
  );

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    rows,
    prepareRow,
    state
  } = tableInstance;

  return (
    <div className="device-list-page">
      <Helmet>
        <title>Pendant List</title>
      </Helmet>
      <h2>Pendant List</h2>
      <div className="loading-message">{loadingMessage}</div>
      <table {...getTableProps()} className="device-list-table">
        <thead>
          {headerGroups.map(headerGroup => (
            <tr {...headerGroup.getHeaderGroupProps()}>
              {headerGroup.headers.map(column => (
                <th {...column.getHeaderProps(column.getSortByToggleProps())}>
                  {column.render('Header')}
                  <div>{column.canFilter ? column.render('Filter') : null}</div>
                  <span>
                    {column.isSorted
                      ? column.isSortedDesc
                        ? ' 🔽'
                        : ' 🔼'
                      : ''}
                  </span>
                </th>
              ))}
            </tr>
          ))}
        </thead>
        <tbody {...getTableBodyProps()}>
          {rows.map(row => {
            prepareRow(row);
            return (
              <tr {...row.getRowProps()}>
                {row.cells.map(cell => (
                  <td {...cell.getCellProps()}>{cell.render('Cell')}</td>
                ))}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

export default PendantListPage;
